import React, { useState } from "react";
import { Helmet } from "react-helmet";

interface FormData {
  name: string;
  email: string;
  phone: string;
  projectDetails: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    projectDetails: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    // Reset form after success
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        projectDetails: ""
      });
    }, 5000);
  };

  const contactInfo = [
    {
      icon: "📞",
      title: "Phone",
      details: ["(123) 456-7890", "(123) 456-7891"],
      action: "tel:+1234567890"
    },
    {
      icon: "📧",
      title: "Email",
      details: ["info@roughframing.com", "quotes@roughframing.com"],
      action: "mailto:info@roughframing.com"
    },
    {
      icon: "📍",
      title: "Address",
      details: ["123 Main Street", "City, State 12345"],
      action: "https://maps.google.com"
    },
    {
      icon: "🕒",
      title: "Business Hours",
      details: ["Mon-Fri: 7:00 AM - 5:00 PM", "Sat: 8:00 AM - 3:00 PM"],
      action: null
    }
  ];



  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Contact Us | Low Cost Framing by Woodhenge Builders | Get Your Free Quote</title>
        <meta name="description" content="Contact Low Cost Framing for your rough framing project. Get a free quote, consultation, or ask questions. Professional framing services backed by Woodhenge Builders." />
        <meta name="keywords" content="rough framing quote, framing contractor contact, Orange County framing, free framing estimate" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/contact-hero.jpg)" }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Contact Low Cost Framing
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 font-light text-amber-100">
              Ready to start your rough framing project? Get your free quote today
            </p>
            <a href="#contact-form">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Multiple ways to reach us for your rough framing project needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl p-8 shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30 text-center group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {info.icon}
                </div>
                <h3 className="text-xl font-bold text-amber-400 mb-4 group-hover:text-amber-300 transition-colors">
                  {info.title}
                </h3>
                <div className="space-y-2">
                  {info.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="text-gray-300">
                      {info.action ? (
                        <a 
                          href={info.action}
                          className="hover:text-amber-400 transition-colors"
                        >
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
                          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Request Your Free Quote
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Fill out the form below and we'll get back to you within 24 hours with a detailed quote for your rough framing project
            </p>
            </div>

            {submitSuccess ? (
              <div className="bg-green-900 border border-green-600 rounded-2xl p-8 text-center">
                <div className="text-4xl mb-4">✅</div>
                <h3 className="text-2xl font-bold text-green-400 mb-4">Thank You!</h3>
                <p className="text-green-300 text-lg">
                  Your message has been sent successfully. We'll get back to you within 24 hours with your free quote.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-gray-800 rounded-2xl p-10 shadow-2xl border border-gray-700">
                <div className="space-y-6 mb-8">
                  <div>
                    <label htmlFor="name" className="block text-amber-400 font-semibold mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-amber-400 font-semibold mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-amber-400 font-semibold mb-3">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>
                
                <div className="mb-8">
                  <label htmlFor="projectDetails" className="block text-amber-400 font-semibold mb-3">
                    Project Details *
                  </label>
                  <textarea
                    id="projectDetails"
                    name="projectDetails"
                    value={formData.projectDetails}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="Tell us about your project, specific requirements, and any questions you have..."
                  />
                </div>
                
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-amber-800 hover:bg-amber-700 disabled:bg-gray-600 text-white font-bold py-4 px-12 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        Sending...
                      </div>
                    ) : (
                      "Submit Your Project Inquiry"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Service Area
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We serve Orange County and surrounding communities with professional rough framing services
            </p>
          </div>
          
          <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
            <div className="h-96 rounded-lg overflow-hidden">
              <iframe
                title="Service Area Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.019123456789!2d-122.419415484681!3d37.7749297797596!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085809c5b5b5b5b%3A0x5b5b5b5b5b5b5b5b!2s123%20Main%20St%2C%20City%2C%20State%2012345!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-300 text-sm">
                📍 123 Main Street, City, State 12345 | 📞 (123) 456-7890
              </p>
              <p className="text-gray-400 text-xs mt-2">
                We serve a 50-mile radius around the metropolitan area
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Common questions about our rough framing services
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
              <h3 className="text-xl font-bold text-amber-400 mb-4">How quickly can you start my project?</h3>
              <p className="text-gray-300 leading-relaxed">
                Project start times vary based on scope and current workload. We typically begin residential projects within 2-4 weeks and commercial projects within 4-8 weeks of contract signing.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
              <h3 className="text-xl font-bold text-amber-400 mb-4">Do you provide free estimates?</h3>
              <p className="text-gray-300 leading-relaxed">
                Yes, we provide detailed, free estimates for all framing projects. Our estimates include material costs, labor, timeline, and any additional considerations for your specific project.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
              <h3 className="text-xl font-bold text-amber-400 mb-4">What areas do you serve?</h3>
              <p className="text-gray-300 leading-relaxed">
                We serve the greater metropolitan area and surrounding communities within a 50-mile radius. Contact us to confirm if we service your specific location.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
              <h3 className="text-xl font-bold text-amber-400 mb-4">Are you licensed and insured?</h3>
              <p className="text-gray-300 leading-relaxed">
                Absolutely. We are fully licensed, bonded, and insured. We carry comprehensive liability insurance and workers' compensation coverage for your protection and peace of mind.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 